import React, { useCallback, useEffect, useState } from 'react';

import { Button, Table } from '@shared/components/bootstrap';

import { Dialog as ErrorDialog } from '@admin/components/errors/dialog';
import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';

import { extractErrorMessage, fetchEditableTicketGroups, fetchTicketGroupings, updateTicketGrouping } from './api';
import { SearchInput } from './search_input';
import { TicketManagementTicketGroup, TicketManagementTicketGrouping } from './types';

export const TicketGroupingsPanel: React.FC = () => {
  const [groups, setGroups] = useState<TicketManagementTicketGroup[]>([]);
  const [groupings, setGroupings] = useState<TicketManagementTicketGrouping[]>();
  const [selection, setSelection] = useState<TicketManagementTicketGrouping | null>(null);
  const [formGroups, setFormGroups] = useState<TicketManagementTicketGroup[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const loadData = useCallback(async () => {
    const [editableGroups, ticketGroupings] = await Promise.all([fetchEditableTicketGroups(), fetchTicketGroupings()]);

    setGroups(editableGroups);
    setGroupings(
      ticketGroupings.map((grouping) => ({
        ...grouping,
        groups: grouping.groups.filter((group) => editableGroups.some((editable) => editable.id === group.id)),
      })),
    );
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const cancel = () => {
    setSelection(null);
    setFormGroups([]);
  };

  const edit = (grouping: TicketManagementTicketGrouping) => {
    setSelection(grouping);
    setFormGroups([...grouping.groups]);
  };

  const assignGroup = (group: TicketManagementTicketGroup) => {
    if (formGroups.some((existing) => existing.id === group.id)) {
      return;
    }

    setFormGroups([...formGroups, group]);
  };

  const unassignGroup = (group: TicketManagementTicketGroup) => {
    setFormGroups(formGroups.filter((existing) => existing.id !== group.id));
  };

  const save = async () => {
    if (!selection) return;

    setSaving(true);
    try {
      await updateTicketGrouping(
        selection.slug,
        formGroups.map((group) => group.id),
      );
      setGroupings((current) =>
        current?.map((grouping) =>
          grouping.slug === selection.slug ? { ...grouping, groups: [...formGroups] } : grouping,
        ),
      );
      cancel();
    } catch (requestError) {
      setError(extractErrorMessage(requestError));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {error && <ErrorDialog title="Whoops" description={error} onClose={() => setError(undefined)} />}

      <Panel id="ticketgroupings">
        <Panel.Header>
          <Panel.Title>Internal Task Types</Panel.Title>
        </Panel.Header>
        <Panel.Body>
          {groupings ? (
            <Table>
              <thead>
                <tr>
                  <th className="col-sm-2 text-left">Type</th>
                  <th className="col-sm-4 text-left">Description</th>
                  <th className="col-sm-4 text-left">Groups</th>
                  <th className="col-sm-2 text-right col-nowrap" />
                </tr>
              </thead>
              <tbody>
                {groupings.map((grouping) => {
                  const editing = selection?.slug === grouping.slug;

                  return (
                    <tr key={grouping.slug}>
                      <td className="col-sm-2 text-left">{grouping.name}</td>
                      <td className="col-sm-4 text-left">{grouping.description}</td>
                      <td className="col-sm-4 text-left">
                        {editing ? (
                          <>
                            <table className="table table-condensed table-nonfluid mar-no">
                              <tbody>
                                {formGroups.map((group) => (
                                  <tr key={group.id}>
                                    <td className="col-zero col-nowrap text-left">
                                      {!group.destroyed ? <span>{group.name}</span> : <del>{group.name}</del>}
                                    </td>
                                    <td className="col-zero col-nowrap text-right">
                                      <button
                                        type="button"
                                        className="btn btn-default"
                                        onClick={() => unassignGroup(group)}
                                      >
                                        -
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <SearchInput options={groups} placeholder="Search" onSelect={assignGroup} />
                          </>
                        ) : (
                          <div>
                            {grouping.groups
                              .filter((group) => !group.destroyed)
                              .map((group) => (
                                <div key={group.id}>{group.name}</div>
                              ))}
                          </div>
                        )}
                      </td>
                      <td className="col-sm-2 text-right col-nowrap">
                        {!editing && (
                          <Button kind="default" onClick={() => edit(grouping)}>
                            Edit
                          </Button>
                        )}
                        {editing && (
                          <>
                            <Button kind="default" onClick={cancel} disabled={saving}>
                              Cancel
                            </Button>{' '}
                            <Button kind="primary" onClick={() => void save()} loading={saving}>
                              Save
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <Spinner />
          )}
        </Panel.Body>
      </Panel>
    </>
  );
};
